import { Button } from "@volca/ui/components/ui/button";
import { Stack } from "@volca/ui/components/ui/stack";
import { cn } from "@volca/ui/lib/utils";
import { useReportCurrency } from "@/profitability/currency";
import { recordKeyColumns } from "@/profitability/records/objects";
import {
  ReportMetadata,
  useReportPresentation,
} from "@/profitability/report-presentation";
import type {
  ReportColumn,
  ReportDataset,
  ReportRow,
} from "@/profitability/types";
import { formatReportValue } from "./utils";

/**
 * What a row is called: the name, what kind of thing it is, and a few of its
 * own fields underneath. A field that repeats what the row is already called,
 * such as an invoice number equal to the job number above it, is dropped: it
 * says nothing twice.
 */
export const TableRowName = ({
  dataset,
  row,
  identifier,
  subtitle,
  details,
  clickable,
}: {
  dataset: ReportDataset;
  row: ReportRow;
  identifier: ReportColumn;
  subtitle?: ReportColumn;
  details: ReportColumn[];
  /** Only where there is a panel to open: a dead link is worse than text. */
  clickable: boolean;
}) => {
  const classic = useReportPresentation() === "classic";
  const currency = useReportCurrency();
  const name = formatReportValue(row[identifier.key], identifier, currency);
  const named = [identifier, subtitle]
    .filter((column) => !!column)
    .map((column) => formatReportValue(row[column.key], column, currency));
  const rest = details
    .map((column) => formatReportValue(row[column.key], column, currency))
    .filter(
      (value, index, all) =>
        !!value && !named.includes(value) && all.indexOf(value) === index,
    );
  // An id ("48217") lines its digits up with the rows above and below.
  const numbered =
    !classic && recordKeyColumns(dataset).includes(identifier.key);
  return (
    <Stack
      gap={0}
      align="stretch"
      className={cn("min-w-0", { "tabular-nums": numbered })}
    >
      {clickable ? (
        <Button
          type="button"
          variant="link"
          className="block h-auto max-w-full truncate p-0 text-left"
          title={name}
        >
          {name}
        </Button>
      ) : (
        <span className="block max-w-full truncate font-medium" title={name}>
          {name}
        </span>
      )}
      {!!subtitle && (
        <span
          className="block truncate text-subtle"
          title={formatReportValue(row[subtitle.key], subtitle, currency)}
        >
          {formatReportValue(row[subtitle.key], subtitle, currency)}
        </span>
      )}
      {!!rest.length && (
        <span
          title={rest.join(classic ? " · " : ", ")}
          className={cn("mt-0.5 block text-subtle", {
            "line-clamp-2": classic,
          })}
        >
          <ReportMetadata>{rest}</ReportMetadata>
        </span>
      )}
    </Stack>
  );
};
